import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, type ViewInstance, type TextInputInstance } from 'react-native';

import { Portal } from '@/components/ui';
import { useAccounts, useBankAccounts, useVendors } from '@/hooks/use-accounting';
import type { Account, BankAccount, Vendor } from '@/types/models';

export type TargetKind = 'customer' | 'vendor' | 'bank';

export interface TargetRef {
	kind: TargetKind;
	id: string;
	/** Display label cached so the input can show it without re-fetching. */
	label: string;
}

export interface TargetTypeaheadProps {
	/** Scopes customers + bank accounts. Vendors are global (no associationId). */
	associationId: string | undefined;
	value: TargetRef | null | undefined;
	onSelect: (target: TargetRef | null) => void;
	/** Free-text override; defaults to `value?.label` once set. */
	value_text?: string;
	onChangeText?: (text: string) => void;
	onTabAdvance?: () => void;
	placeholder?: string;
	autoFocus?: boolean;
	testID?: string;
	inputRef?: React.RefObject<TextInputInstance | null>;
	disabled?: boolean;
	inputStyle?: any;
}

const KIND_LABEL: Record<TargetKind, string> = {
	customer: 'CUST',
	vendor: 'VEND',
	bank: 'BANK',
};

const KIND_COLOR: Record<TargetKind, string> = {
	customer: '#1565c0',
	vendor: '#6a1b9a',
	bank: '#2e7d32',
};

const display = (t: TargetRef): string => `${KIND_LABEL[t.kind]} ${t.label}`;

/**
 * Flatten a `TargetRef` into the mutually-exclusive sub-ledger pointer fields
 * that `journal_lines` carries (accountId / vendorId / bankAccountId). The xor
 * is enforced server-side by `JournalLineInputSchema`; this helper keeps the
 * mapping in one place so callers don't reinvent the ternary chain.
 */
export const targetRefToLineFields = (
	t: TargetRef | null | undefined,
): { accountId?: string; vendorId?: string; bankAccountId?: string } => {
	if (!t) return {};
	switch (t.kind) {
		case 'customer': return { accountId: t.id };
		case 'vendor': return { vendorId: t.id };
		case 'bank': return { bankAccountId: t.id };
	}
};

interface Candidate {
	kind: TargetKind;
	id: string;
	label: string;
	haystack: string;
}

const accountLabel = (a: Account): string => {
	const num = a.accountNumber ? `#${a.accountNumber}` : '';
	const addr = a.address ?? '';
	return [num, addr].filter(Boolean).join(' · ') || a.id;
};

const vendorLabel = (v: Vendor): string => v.printAs || v.name || v.companyName || v.id;

const bankLabel = (b: BankAccount): string => {
	const tail = b.accountNumberLast4 ? ` ••${b.accountNumberLast4}` : '';
	return `${b.name}${tail}`;
};

/**
 * Combined typeahead over the three things a journal-line target can be:
 * an AR customer (accounts), an AP vendor, or a bank account. Mirrors
 * `GlCodeTypeahead`: ArrowUp/Down moves highlight; Tab commits and lets the
 * browser advance focus; Enter explicitly commits without advancing; Escape
 * closes. Each match row carries a colored kind chip so the user can tell at
 * a glance which sub-ledger they're aiming at.
 */
export const TargetTypeahead: React.FC<TargetTypeaheadProps> = ({
	associationId,
	value,
	onSelect,
	value_text,
	onChangeText,
	onTabAdvance,
	placeholder = 'Target (optional)…',
	autoFocus,
	testID,
	inputRef,
	disabled,
	inputStyle,
}) => {
	const isControlled = value_text !== undefined;
	const [internalText, setInternalText] = useState('');
	const text = isControlled ? (value_text as string) : internalText;
	const setText = useCallback((t: string) => {
		if (!isControlled) setInternalText(t);
		onChangeText?.(t);
	}, [isControlled, onChangeText]);

	const [highlight, setHighlight] = useState(0);
	const [open, setOpen] = useState(false);
	const wrapRef = useRef<ViewInstance>(null);
	const ownInputRef = useRef<TextInputInstance>(null);
	const effectiveInputRef = inputRef ?? ownInputRef;
	const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number } | null>(null);

	const { data: acctResp } = useAccounts(
		associationId ? { associationId, limit: 500 } : { limit: 0 },
	);
	const { data: vendResp } = useVendors({ activeOnly: true, limit: 500 });
	const { data: bankResp } = useBankAccounts(
		associationId ? { associationId, activeOnly: true, limit: 100 } : { limit: 0 },
	);

	const candidates = useMemo<Candidate[]>(() => {
		const accts = ((acctResp?.data ?? []) as Account[]).map((a) => {
			const label = accountLabel(a);
			return { kind: 'customer' as const, id: a.id, label, haystack: label.toLowerCase() };
		});
		const vends = ((vendResp?.data ?? []) as Vendor[]).map((v) => {
			const label = vendorLabel(v);
			return { kind: 'vendor' as const, id: v.id, label, haystack: label.toLowerCase() };
		});
		const banks = ((bankResp?.data ?? []) as BankAccount[]).map((b) => {
			const label = bankLabel(b);
			return { kind: 'bank' as const, id: b.id, label, haystack: label.toLowerCase() };
		});
		return [...accts, ...vends, ...banks];
	}, [acctResp, vendResp, bankResp]);

	useEffect(() => {
		if (isControlled) return;
		if (!value) return;
		if (display(value) !== internalText) setInternalText(display(value));
	}, [value, isControlled, internalText]);

	const matches = useMemo(() => {
		const q = text.trim().toLowerCase();
		if (!q) return [] as Candidate[];
		if (value && display(value).toLowerCase() === q) return [];
		return candidates.filter((c) => c.haystack.includes(q)).slice(0, 12);
	}, [candidates, text, value]);

	useEffect(() => { setHighlight(0); }, [text]);

	useEffect(() => {
		if (!text.trim() && value) onSelect(null);
	}, [text, value, onSelect]);

	const measureAnchor = useCallback(() => {
		const node = wrapRef.current as unknown as {
			getBoundingClientRect?: () => DOMRect;
			measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
		} | null;
		if (!node) return;
		if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
			const r = node.getBoundingClientRect();
			setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
		} else if (typeof node.measureInWindow === 'function') {
			node.measureInWindow((x, y, w, h) => setAnchorRect({ top: y + h, left: x, width: w }));
		}
	}, []);

	useEffect(() => {
		if (!open) return;
		measureAnchor();
		if (Platform.OS !== 'web') return;
		const onMove = () => measureAnchor();
		window.addEventListener('scroll', onMove, true);
		window.addEventListener('resize', onMove);
		return () => {
			window.removeEventListener('scroll', onMove, true);
			window.removeEventListener('resize', onMove);
		};
	}, [open, measureAnchor, matches.length]);

	const commit = (c: Candidate, advance: boolean) => {
		const ref: TargetRef = { kind: c.kind, id: c.id, label: c.label };
		setText(display(ref));
		onSelect(ref);
		setOpen(false);
		if (advance) onTabAdvance?.();
	};

	return (
		<View ref={wrapRef} style={styles.wrap}>
			<TextInput
				ref={effectiveInputRef}
				value={text}
				onChangeText={(v) => { setText(v); setOpen(true); }}
				autoFocus={autoFocus}
				autoCorrect={false}
				autoCapitalize="none"
				editable={!disabled}
				placeholder={placeholder}
				placeholderTextColor="#b0bec5"
				style={[styles.input, inputStyle, disabled && styles.inputDisabled]}
				onFocus={() => setOpen(true)}
				onBlur={() => setTimeout(() => setOpen(false), 120)}
				testID={testID}
				onKeyPress={(e: any) => {
					const key = e.nativeEvent?.key as string | undefined;
					if (key === 'ArrowDown') {
						e.preventDefault?.();
						setHighlight((h) => Math.min(matches.length - 1, h + 1));
					} else if (key === 'ArrowUp') {
						e.preventDefault?.();
						setHighlight((h) => Math.max(0, h - 1));
					} else if (key === 'Tab') {
						const top = matches[highlight] ?? matches[0];
						if (top) commit(top, true);
					} else if (key === 'Enter') {
						const top = matches[highlight] ?? matches[0];
						if (top) {
							e.preventDefault?.();
							commit(top, false);
						}
					} else if (key === 'Escape') {
						setOpen(false);
					}
				}}
			/>
			{open && matches.length > 0 && anchorRect && (
				<Portal>
					<View
						testID={testID ? `${testID}-dropdown` : 'target-typeahead-dropdown'}
						style={{
							position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
							top: anchorRect.top + 2,
							left: anchorRect.left,
							width: Math.max(anchorRect.width, 320),
							zIndex: 99999,
							maxHeight: 320,
						}}
					>
						<View style={styles.dropdown}>
							<ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="always" nestedScrollEnabled>
								{matches.map((c, idx) => (
									<Pressable
										key={`${c.kind}:${c.id}`}
										onPress={() => commit(c, true)}
										style={[styles.option, idx === highlight && styles.optionActive]}
									>
										<View style={[styles.kindChip, { backgroundColor: KIND_COLOR[c.kind] }]}>
											<Text style={styles.kindChipText}>{KIND_LABEL[c.kind]}</Text>
										</View>
										<Text style={styles.optName} numberOfLines={1}>{c.label}</Text>
									</Pressable>
								))}
							</ScrollView>
						</View>
					</View>
				</Portal>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	wrap: { position: 'relative', flex: 1, alignSelf: 'stretch' },
	input: {
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		padding: 10, fontSize: 14, backgroundColor: '#fff',
		width: '100%',
	},
	inputDisabled: { backgroundColor: '#f5f5f5', color: '#9e9e9e' },
	dropdown: {
		backgroundColor: '#fff',
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 12, shadowOffset: { width: 0, height: 6 },
		elevation: 24,
		overflow: 'hidden',
	},
	option: {
		flexDirection: 'row', alignItems: 'center', gap: 10,
		paddingVertical: 8, paddingHorizontal: 10,
		borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#eee',
	},
	optionActive: { backgroundColor: '#e3f2fd' },
	kindChip: {
		paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, minWidth: 44, alignItems: 'center',
	},
	kindChipText: { color: '#fff', fontSize: 10, fontWeight: '700', letterSpacing: 0.5 },
	optName: { color: '#263238', fontSize: 13, fontWeight: '600', flex: 1 },
});
